/**
 * The notification hub, the platform's half (mju-ai-workbench
 * docs/plans/2026-09-project-model-end-to-end.md, R3). One record, produced everywhere
 * (a host, a desktop, a node, the organisation), delivered everywhere (the workbench, the
 * console, the phone). A platform project's inbox is kept here, and the workbench files and
 * answers as a client; a project in the workbench's Personal space keeps its inbox on the desktop.
 *
 *   POST  /projects/v1/:id/notifications              file one (create, or upsert by dedupeKey)
 *   GET   /projects/v1/:id/notifications?since=<seq>&state=&limit=   { seq, items }
 *   PATCH /projects/v1/:id/notifications/:nid          { ifVersion, state } → the record; 409 { current }
 *   POST  /projects/v1/:id/notifications/:nid/act      { actionId } → runs the action, marks it done
 *   GET   /notifications/v1?state=unread&limit=        the person's inbox across their projects
 *   PUT   /notifications/v1/push-tokens                { token, platform } register this phone
 *   DELETE /notifications/v1/push-tokens/:token
 *
 * A record's `seq` comes from the project's board sequence, and every write is also a
 * `notification` event on `GET /projects/v1/:id/board-stream`.
 */
import { z } from "zod";
export const NOTIFICATION_KINDS = ["approval", "decision", "question", "done", "failed", "changed", "mention", "update", "policy", "budget", "node", "info"];
export const notificationKindSchema = z.enum(NOTIFICATION_KINDS);
/** needs-you: an action waits on a person; for-you: about their work; fyi: the inbox only. */
export const notificationAttentionSchema = z.enum(["needs-you", "for-you", "fyi"]);
/**
 * `snoozed` is a person's own: the record reads as unread again once `until` passes. A record
 * addressed to exactly one person keeps one state; a record for everyone, or for a role or group,
 * keeps read, done and snoozed per person (an act, or the organisation marking it done, closes it
 * for everyone), so one member's Done never clears another's.
 */
export const notificationStateSchema = z.enum(["unread", "read", "done", "expired", "snoozed"]);
export const notificationSourceSchema = z.enum(["host", "desktop", "organisation", "node"]);
/** Who a record is for, when it is for someone in particular: a person, an IdP group, or a tenant role. */
export const notificationTargetSchema = z.object({
    subjectType: z.enum(["user", "group", "role"]),
    subjectId: z.string().min(1).max(200),
});
/**
 * What a person can do about it. `rpc` is a Control API method the source device serves
 * (`permissions.resolve`, …), or for the organisation's own records a platform action
 * (`aioe.promotion.decide`), run by `POST …/act`.
 */
export const notificationActionSchema = z.object({
    id: z.string().min(1).max(64),
    label: z.string().min(1).max(60),
    rpc: z.string().min(1).max(120),
    /** Passed to the rpc as sent. `keepOpen: true` marks one of several choices: acting on it leaves the record open (the workbench refiles it without that button); a choice without it closes the record. */
    params: z.record(z.string(), z.unknown()).optional(),
});
/** Where a record's live session runs (D282): `GET /people/v1/me/live` lists the same session by these. */
export const notificationLiveSchema = z.object({
    deviceId: z.string().min(1).max(100),
    conversationId: z.string().min(1).max(200).optional(),
    sessionId: z.string().min(1).max(200).optional(),
});
export const notificationSchema = z.object({
    id: z.string(),
    projectId: z.string(),
    at: z.string(),
    kind: notificationKindSchema,
    attention: notificationAttentionSchema,
    title: z.string().min(1).max(300),
    body: z.string().max(4000).optional(),
    rootId: z.string().max(200).optional(),
    /** The repository's name in words (AIOE 0.34.0), put on every answer that carries a `rootId`; never stored. */
    rootName: z.string().max(200).optional(),
    teamId: z.string().max(200).optional(),
    /** The team's name for people (workbench D239), beside `teamId`: the Inbox names the team, never its id. */
    teamName: z.string().max(200).optional(),
    taskId: z.string().max(100).optional(),
    /** An approval's risk, 1 to 5, with its words ("Needs approval"): the Inbox says "Risk level 4 out of 5: needs approval". */
    riskLevel: z.number().int().min(1).max(5).optional(),
    riskWords: z.string().max(100).optional(),
    sessionId: z.string().max(200).optional(),
    /** The run the record is about (AIOE 0.40.0, additive): the run ledger's id, the same one the audit trail and the exports carry. */
    runId: z.string().max(200).optional(),
    /** An approval at risk 4 or 5 (D270): only an owner of the project may approve it, so the record offers no Approve (D282, additive). */
    ownerRequired: z.boolean().optional(),
    /**
     * Additive (workbench 0.89.17): the machine's policy keeps approvals of this risk off remote clients, so only its desktop
     * can answer — the words to show in place of Approve ("Only this desktop can approve this: …"). The allow actions stay
     * on the record for the desktop's own Inbox.
     */
    desktopOnly: z.string().max(300).optional(),
    /**
     * D282 (additive): the live session the record is about, so the console and the phone can open its Live view
     * ("Open live"): the machine, and the conversation (Chat, Research, Team Work) or, for an agent of its own, the session.
     */
    live: notificationLiveSchema.optional(),
    /** For these people in particular (any of them); absent or empty means everyone on the project. */
    to: z.array(notificationTargetSchema).max(50).optional(),
    by: z.object({ principal: z.string().max(200).optional(), actor: z.string().max(200).optional() }),
    actions: z.array(notificationActionSchema).max(8),
    state: notificationStateSchema,
    /** State `snoozed`: when it comes back. */
    until: z.string().optional(),
    /** The same key filed again replaces the record (an approval's second reminder, a task's next change). */
    dedupeKey: z.string().max(300).optional(),
    source: notificationSourceSchema,
    /** The device the record came from, and the one its actions are sent to. */
    sourceDeviceId: z.string().max(100).optional(),
    /**
     * The person who filed it through the API (absent when the platform filed it). Only they, or the
     * platform, may replace it by id or dedupe key; its organisation actions run with someone else's
     * authority only when the platform or an administrator filed it.
     */
    filedBy: z.string().max(200).optional(),
    version: z.number().int().positive(),
    seq: z.number().int().nonnegative(),
    updatedAt: z.string(),
});
/** `POST /projects/v1/:id/notifications`. The source and its device are taken from the token. */
export const notificationCreateSchema = notificationSchema
    .omit({ id: true, projectId: true, version: true, seq: true, updatedAt: true, source: true, sourceDeviceId: true, filedBy: true, state: true, by: true, at: true })
    .extend({
    /** A record answered where it was raised is filed already done. */
    state: notificationStateSchema.default("unread"),
    at: z.string().optional(),
    by: z.object({ principal: z.string().max(200).optional(), actor: z.string().max(200).optional() }).optional(),
    actions: z.array(notificationActionSchema).max(8).default([]),
});
/**
 * `POST /notifications/v1/state`: many records at once, as the person (mark all as read, a selection done).
 * Each item is checked as the single PATCH is; one that is gone, not the person's to see or at another version
 * is reported, never fatal. `read` and `done` only: a snooze is one record's.
 */
export const notificationBulkStateSchema = z
    .object({
    state: z.enum(["read", "done"]),
    items: z.array(z.object({ projectId: z.string().min(1).max(100), id: z.string().min(1).max(100), ifVersion: z.number().int().positive() })).min(1).max(200).optional(),
    /** Instead of `items`: every unread record the person may see (`read` only), in one project or across all of them. */
    all: z.literal(true).optional(),
    projectId: z.string().min(1).max(100).optional(),
})
    .refine((b) => (b.items !== undefined) !== (b.all === true), "send items, or all: true")
    .refine((b) => !b.all || b.state === "read", "all marks read; done is a chosen record's");
export const notificationPatchSchema = z
    .object({
    ifVersion: z.number().int().positive(),
    state: notificationStateSchema,
    /** With `snoozed`: when to bring it back (ISO, in the future). */
    until: z.string().optional(),
})
    .refine((p) => p.state !== "snoozed" || !!p.until, { message: "snoozed needs until" });
/** Whether a record's read, done and snoozed state is each person's own, or one for all. */
export const perPersonState = (n) => !(n.to?.length === 1 && n.to[0].subjectType === "user");
export const notificationActSchema = z.object({ actionId: z.string().min(1).max(64) });
/** A target as one string, for storage and comparison: `user:<id>`, `group:<id>`, `role:<name>`. */
export const targetKey = (t) => `${t.subjectType}:${t.subjectId}`;
export const pushTokenSchema = z.object({
    /** An Expo push token (`ExponentPushToken[…]`). */
    token: z.string().min(8).max(300),
    platform: z.enum(["ios", "android", "web"]),
    /** For the person's own list: "JP's phone". */
    name: z.string().max(100).optional(),
});
//# sourceMappingURL=notifications.js.map